import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Button, Alert } from 'reactstrap';
import { Bell, BellOff, CheckCircle, XCircle } from 'react-feather';
import usePushNotifications from '../../../hooks/usePushNotifications';
import DeviceSubscriptions from './DeviceSubscriptions';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const NotificationSettings = () => {
  const user = useSelector((state) => state.User);
  const [refreshSubscriptions, setRefreshSubscriptions] = useState(0);
  
  const {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    showNotification
  } = usePushNotifications();

  const [isToggling, setIsToggling] = useState(false);

  const handleToggleNotifications = async () => {
    try {
      setIsToggling(true);
      
      if (isSubscribed) {
        await unsubscribe();
        toast.success('Notificaciones desactivadas', { position: 'bottom-right' });
      } else {
        await subscribe();
        toast.success('Notificaciones activadas', { position: 'bottom-right' });
        setRefreshSubscriptions(prev => prev + 1);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`, { position: 'bottom-right' });
    } finally {
      setIsToggling(false);
    }
  };

  const handleSubscriptionRemoved = () => {
    setRefreshSubscriptions(prev => prev + 1);
  };

  if (!isSupported) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;
    
    return (
      <Card>
        <CardHeader>
          <h5>Configuración de Notificaciones</h5>
        </CardHeader>
        <CardBody>
          {isIOSSafari ? (
            <div>
              <Alert color="info" className="mb-3">
                <Bell className="me-2" size={20} />
                <strong>¡Mejora tu experiencia!</strong>
              </Alert>
              
              <div className="text-center">
                <div className="mb-3">
                  <i className="pe-7s-phone pe-3x text-primary"></i>
                </div>
                
                <h6 className="mb-3">Instala la app para recibir notificaciones</h6>
                
                <p className="text-muted mb-4">
                  Las notificaciones push no están disponibles en Safari móvil, 
                  pero puedes instalar nuestra app para recibir notificaciones 
                  directamente en tu iPhone.
                </p>
                
                <div className="d-grid gap-2">
                  <Button 
                    color="primary" 
                    size="lg"
                    onClick={() => {
                      // Mostrar instrucciones de instalación más detalladas
                      const instructions = `
📱 CÓMO INSTALAR LA APP:

1️⃣ Toca el botón "Compartir" (📤) en la parte inferior de Safari

2️⃣ Desplázate hacia abajo y selecciona "Agregar a pantalla de inicio"

3️⃣ Personaliza el nombre si quieres y toca "Agregar"

4️⃣ ¡Listo! Ahora tendrás la app en tu pantalla de inicio

💡 Una vez instalada, podrás recibir notificaciones push
                      `;
                      alert(instructions);
                    }}
                  >
                    <i className="pe-7s-download pe-2"></i>
                    Instalar App
                  </Button>
                  
                  <Button 
                    color="outline-secondary" 
                    onClick={() => {
                      // Mostrar instrucciones para Chrome
                      const chromeInstructions = `
🌐 USAR CHROME EN TU IPHONE:

1️⃣ Descarga Chrome desde el App Store si no lo tienes

2️⃣ Abre Chrome y ve a: ${window.location.href}

3️⃣ En Chrome, las notificaciones push sí funcionan

4️⃣ Ve a Configuración → Notificaciones para activarlas

💡 Chrome en iPhone soporta notificaciones push web
                      `;
                      alert(chromeInstructions);
                    }}
                  >
                    <i className="pe-7s-global pe-2"></i>
                    Usar Chrome
                  </Button>
                </div>
                
                <div className="mt-4">
                  <small className="text-muted">
                    <i className="pe-7s-info pe-1"></i>
                    También puedes usar Chrome en tu iPhone para habilitar notificaciones
                  </small>
                </div>
              </div>
            </div>
          ) : (
            <Alert color="warning">
              <XCircle className="me-2" size={20} />
              Las notificaciones push no son compatibles con este navegador.
            </Alert>
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <h5>Configuración de Notificaciones</h5>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert color="danger" className="mb-3">
              <XCircle className="me-2" size={20} />
              {error}
            </Alert>
          )}
          
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              {isSubscribed ? (
                <CheckCircle className="me-2 text-success" size={20} />
              ) : (
                <XCircle className="me-2 text-danger" size={20} />
              )}
              <div>
                <h6 className="mb-0">
                  {isSubscribed ? 'Notificaciones Activadas' : 'Notificaciones Desactivadas'}
                </h6>
                <small className="text-muted">
                  {isSubscribed 
                    ? 'Recibirás notificaciones de nuevos avisos y tareas'
                    : 'No recibirás notificaciones push'
                  }
                </small>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                color={isSubscribed ? 'danger' : 'success'}
                onClick={handleToggleNotifications}
                disabled={isLoading || isToggling}
                className="d-flex align-items-center"
              >
                {isLoading || isToggling ? (
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                ) : isSubscribed ? (
                  <BellOff className="me-2" size={16} />
                ) : (
                  <Bell className="me-2" size={16} />
                )}
                {isSubscribed ? 'Desactivar' : 'Activar'}
              </Button>
              
              {isSubscribed && (
                <Button
                  color="info"
                  onClick={() => showNotification('Prueba', { body: 'Esta es una notificación de prueba' })}
                  className="d-flex align-items-center"
                >
                  <Bell className="me-2" size={16} />
                  Probar
                </Button>
              )}
            </div>
          </div>
          
          <div className="mt-3">
            <small className="text-muted">
              <strong>Notificaciones que recibirás:</strong>
              <ul className="mt-2 mb-0">
                <li>Nuevos avisos (todos los usuarios)</li>
                <li>Nuevas tareas (solo tutores)</li>
              </ul>
            </small>
          </div>
        </CardBody>
      </Card>
      
      {/* Device Subscriptions List */}
      {isSubscribed && user.uuid && (
        <div className="mt-4">
          <DeviceSubscriptions 
            userUuid={user.uuid} 
            onSubscriptionRemoved={handleSubscriptionRemoved}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
